import { computed, signal, Signal } from '@preact/signals';
import * as ECAPI from './EcommerceService.ts';
import type { Subscription } from '@/domain/Subscription';
import type { FavoriteProducts } from '@/domain/Product';

let cartApi: CartApi | undefined;
let cartManager: Signal<CartManager | undefined>;

// Cart ID docs:
// https://nestle.atlassian.net/wiki/spaces/SFSR/pages/76605395/Frontend+Introduce+Cart+uuid+universally+unique+identifier
// https://nestle.atlassian.net/wiki/spaces/SFSR/pages/264209461/Mobile+-+Migrate+Carts+API+to+v4+from+v3
let cartId: string | undefined;

// Tracks the most recent cart update promise to prevent race conditions
// when multiple updates are triggered in quick succession
let pendingCartUpdatePromise: Promise<boolean> = Promise.resolve(true);

const POLL_CART_MANAGER_TIMEOUT = 500;

export const cart = signal<Cart | undefined>(undefined);
export const isLoading = signal(false);
export const isReady = computed(() => cartManager.value !== undefined);

export async function setCartManager(cartMan: CartManager) {
  cartManager = signal(cartMan);
}

export function initCart(napi: NAPI = window.napi): () => void {
  cartApi = napi?.cart();
  cartManager = signal<CartManager | undefined>(undefined);

  // consider moving this to a different function
  const cleanupInterval = setInterval(() => {
    if (window.CartManager) {
      clearInterval(cleanupInterval);
      cartManager.value = window.CartManager;
    }
  }, POLL_CART_MANAGER_TIMEOUT);

  napi?.data().on('cart.update', updateCart);
  updateCart();

  return () => {
    clearInterval(cleanupInterval);
    napi?.data().off('cart.update', updateCart);
  };
}

export async function addSubscription(
  productId?: string,
  subscriptionId?: string
): Promise<boolean> {
  if (!productId || !subscriptionId) {
    return false;
  }

  if (!cartManager.value) {
    return false;
  }

  await cartManager.value.addSubscription(subscriptionId, productId);
  await updateCart();

  return true;
}

export async function removeSubscription(): Promise<boolean> {
  if (!cartManager.value) {
    return false;
  }

  await cartManager.value.removeSubscription();
  await updateCart();

  return true;
}

export async function updateItem(
  productId: string,
  amount: number
): Promise<boolean> {
  // Wait for any pending updates to complete before proceeding
  await pendingCartUpdatePromise;

  // Create and track the new update promise
  pendingCartUpdatePromise = (async () => {
    if (!cartManager.value) {
      return false;
    }

    await cartManager.value.updateItem(productId, amount, null, null, false);

    await updateCart();

    return true;
  })();

  return pendingCartUpdatePromise;
}

async function updateCart(): Promise<void> {
  cart.value = await cartApi?.read({ forceFull: true });
}

export function getSubscription(
  subscriptionsByDuration: {
    [duration: string]: Subscription[];
  },
  subscriptionId = cart.value?.subscription?.profileId
): Subscription | false {
  if (!subscriptionId) {
    return false;
  }

  return (
    Object.values(subscriptionsByDuration)
      .flat()
      .find(
        (subscription: Subscription) => subscriptionId === subscription.id
      ) || false
  );
}

export async function isProductAvailable(productId: string): Promise<boolean> {
  if (!cartManager?.value) {
    return false;
  }

  const productDetails = await window?.napi.catalog().getProduct(productId);
  const { isOrderable } = productDetails;
  const stocks = await window?.napi?.catalog().getStocks();
  const inStock = stocks[productId];

  if (!productDetails || !inStock || !isOrderable) {
    return false;
  }

  return true;
}

export async function getFavoriteProducts(
  market: string,
  channel: string,
  cacheBurst: boolean = false
): Promise<FavoriteProducts> {
  const { id, lines } = await ECAPI.getFavoriteCart(
    market,
    channel,
    cacheBurst
  );
  cartId = id;
  return {
    cartId: id,
    favoriteProducts: new Set(lines.map((line) => line.item))
  };
}

export async function setFavoriteProducts(
  market: string,
  channel: string,
  favoriteProducts: Set<string>
): Promise<FavoriteProducts> {
  const lines = Array.from(favoriteProducts).map((productId) => ({
    item: productId,
    quantity: 1
  }));
  const res = await ECAPI.setFavoriteProducts(market, channel, cartId, lines);

  // The cart can be created at this moment
  cartId = res.id;
  return {
    cartId: res.id,
    favoriteProducts: new Set(res.lines.map((line) => line.item))
  };
}
